import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Briefcase,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  Activity,
  Users,
  Target,
  ArrowRight,
  RefreshCw,
  Calendar,
  BarChart3,
  PieChart,
  AlertCircle,
  Timer,
  Award
} from 'lucide-react';
import {
  PieChart as RePieChart, Pie, Cell, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip
} from 'recharts';

const ESTADO_CONFIG = {
  ABIERTO: { label: 'Abierto', color: 'bg-blue-100 text-blue-700' },
  EN_INVESTIGACION: { label: 'En Investigación', color: 'bg-amber-100 text-amber-700' },
  PENDIENTE_APROBACION: { label: 'Pend. Aprobación', color: 'bg-purple-100 text-purple-700' },
  CERRADO: { label: 'Cerrado', color: 'bg-green-100 text-green-700' },
  ARCHIVADO: { label: 'Archivado', color: 'bg-slate-100 text-slate-700' }
};

const PRIORIDAD_CONFIG = {
  CRITICA: { label: 'Crítica', color: 'bg-red-500' },
  ALTA: { label: 'Alta', color: 'bg-orange-500' },
  MEDIA: { label: 'Media', color: 'bg-amber-500' },
  BAJA: { label: 'Baja', color: 'bg-green-500' }
};

const COLORS = ['#7c3aed', '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#64748b'];

const MisCasos = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const res = await api.get('/cases/my-dashboard');
      setDashboard(res.data);
    } catch (error) {
      toast.error('Error al cargar el dashboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <RefreshCw className="animate-spin h-12 w-12 text-purple-600 mx-auto" />
          <p className="mt-4 text-slate-600">Cargando tu dashboard...</p>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <AlertCircle size={48} className="text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">No se pudo cargar el dashboard</p>
          <Button onClick={loadDashboard} className="mt-4">Reintentar</Button>
        </div>
      </div>
    );
  }

  const { resumen, alertas, tiempos, por_estado, por_prioridad, casos_recientes } = dashboard;

  // Datos para gráficos
  const estadoData = Object.entries(por_estado).map(([key, value]) => ({
    name: ESTADO_CONFIG[key]?.label || key,
    value,
    color: key === 'ABIERTO' ? '#3b82f6' : 
           key === 'EN_INVESTIGACION' ? '#f59e0b' : 
           key === 'CERRADO' ? '#22c55e' : 
           key === 'PENDIENTE_APROBACION' ? '#8b5cf6' : '#64748b'
  }));

  const prioridadData = Object.entries(por_prioridad).map(([key, value]) => ({
    name: PRIORIDAD_CONFIG[key]?.label || key,
    value,
    fill: key === 'CRITICA' ? '#ef4444' : 
          key === 'ALTA' ? '#f97316' : 
          key === 'MEDIA' ? '#f59e0b' : '#22c55e'
  }));

  return (
    <div className="p-6 lg:p-8 space-y-6 h-full overflow-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-slate-900">
            Mis Casos
          </h1>
          <p className="text-slate-600 mt-1">
            Dashboard de rendimiento y métricas de resolución
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={loadDashboard}>
            <RefreshCw size={16} className="mr-2" />
            Actualizar
          </Button>
          <Button 
            onClick={() => navigate('/m/gestion-de-casos/casos')}
            style={{ backgroundColor: '#7c3aed' }}
          >
            <Briefcase size={16} className="mr-2" />
            Ver Todos los Casos
          </Button>
        </div>
      </div>

      {/* Métricas Principales */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-5 bg-gradient-to-br from-purple-50 to-white border-purple-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Briefcase size={20} className="text-purple-600" />
            </div>
            <span className="text-sm text-slate-500">Total Casos</span>
          </div>
          <p className="text-3xl font-bold text-purple-700">{resumen.total_casos}</p>
          <p className="text-xs text-purple-600 mt-1">
            +{resumen.casos_nuevos_semana} esta semana
          </p>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-blue-50 to-white border-blue-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Activity size={20} className="text-blue-600" />
            </div>
            <span className="text-sm text-slate-500">Casos Activos</span>
          </div>
          <p className="text-3xl font-bold text-blue-700">{resumen.casos_abiertos}</p>
          <p className="text-xs text-blue-600 mt-1">
            {resumen.casos_pendientes_aprobacion} pendientes aprobación
          </p>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-green-50 to-white border-green-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle size={20} className="text-green-600" />
            </div>
            <span className="text-sm text-slate-500">Casos Cerrados</span>
          </div>
          <p className="text-3xl font-bold text-green-700">{resumen.casos_cerrados}</p>
          <p className="text-xs text-green-600 mt-1">
            {Math.round((resumen.casos_cerrados / Math.max(1, resumen.total_casos)) * 100)}% completados
          </p>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-amber-50 to-white border-amber-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Timer size={20} className="text-amber-600" />
            </div>
            <span className="text-sm text-slate-500">Tiempo Promedio</span>
          </div>
          <p className="text-3xl font-bold text-amber-700">{tiempos.promedio_resolucion_texto}</p>
          <p className="text-xs text-amber-600 mt-1">
            de resolución por alerta
          </p>
        </Card>
      </div>

      {/* Métricas de Alertas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Progreso de Resolución */}
        <Card className="p-6 bg-white lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Target size={20} className="text-purple-600" />
              Resolución de Alertas
            </h3>
            <Badge className="bg-purple-100 text-purple-700">
              {alertas.tasa_resolucion}% completado
            </Badge>
          </div>
          
          <div className="mb-6">
            <Progress value={alertas.tasa_resolucion} className="h-3" />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg text-center">
              <AlertTriangle size={24} className="text-slate-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-700">{alertas.total}</p>
              <p className="text-xs text-slate-500">Total Alertas</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg text-center">
              <CheckCircle size={24} className="text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-700">{alertas.resueltas}</p>
              <p className="text-xs text-green-600">Resueltas</p>
            </div>
            <div className="p-4 bg-amber-50 rounded-lg text-center">
              <Clock size={24} className="text-amber-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-amber-700">{alertas.pendientes}</p>
              <p className="text-xs text-amber-600">Pendientes</p>
            </div>
          </div>

          {alertas.tasa_resolucion >= 80 && (
            <div className="mt-4 p-3 bg-green-50 rounded-lg flex items-center gap-3">
              <Award size={20} className="text-green-600" />
              <p className="text-sm text-green-700">
                ¡Excelente trabajo! Mantienes una alta tasa de resolución
              </p>
            </div>
          )}
        </Card>

        {/* Distribución por Estado */}
        <Card className="p-6 bg-white">
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <PieChart size={20} className="text-blue-600" />
            Por Estado
          </h3>
          {estadoData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <RePieChart>
                <Pie
                  data={estadoData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {estadoData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </RePieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-slate-400">
              Sin datos
            </div>
          )}
          <div className="space-y-2 mt-4">
            {estadoData.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></span>
                  <span className="text-slate-600">{item.name}</span>
                </div>
                <span className="font-medium text-slate-700">{item.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Distribución por Prioridad */}
        <Card className="p-6 bg-white">
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <BarChart3 size={20} className="text-amber-600" />
            Por Prioridad
          </h3>
          {prioridadData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={prioridadData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" width={80} />
                <Tooltip />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-slate-400">
              Sin datos
            </div>
          )}
        </Card>

        {/* Casos Recientes */}
        <Card className="p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Calendar size={20} className="text-purple-600" />
              Casos Recientes
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/m/gestion-de-casos/casos')}
              className="text-purple-600"
            >
              Ver todos
              <ArrowRight size={14} className="ml-1" />
            </Button>
          </div>
          <div className="space-y-3">
            {casos_recientes.length > 0 ? (
              casos_recientes.map((caso, idx) => (
                <div
                  key={caso.id || idx}
                  className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                  onClick={() => navigate(`/m/gestion-de-casos/caso/${caso.id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-slate-800 text-sm line-clamp-1">{caso.titulo}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={ESTADO_CONFIG[caso.estado]?.color || 'bg-slate-100 text-slate-700'} variant="secondary">
                          {ESTADO_CONFIG[caso.estado]?.label || caso.estado}
                        </Badge>
                        {caso.alertas > 0 && (
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <AlertTriangle size={12} />
                            {caso.alertas} alertas
                          </span>
                        )}
                      </div>
                    </div>
                    <ArrowRight size={16} className="text-slate-400" />
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-6 text-slate-400">
                <Briefcase size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No tienes casos asignados</p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default MisCasos;
